import { buildLocalePrefix, parseLocaleFromPath } from "@/i18n/LocaleContext";
import { safeRedirectPath } from "@/lib/safeRedirect";

export const DRIVER_INVITATION_CONTINUATION_KEY =
  "bac:driver-invitation-signout-continuation";
export const DRIVER_INVITATION_CONTINUATION_TTL_MS = 10 * 60 * 1000;

/**
 * Validate the only invitation URL allowed through the intentional
 * account-switch sign-out. The current flow keeps it in memory until it is
 * placed in the explicit login URL; the bounded session record below is only
 * a fallback for older clients. It is never sent to analytics.
 */
export function safeDriverInvitationPath(value: string | null | undefined): string | null {
  const safePath = safeRedirectPath(value);
  if (!value || safePath === "/") return null;

  try {
    const url = new URL(safePath, "https://bookaclassic.invalid");
    const { innerPath } = parseLocaleFromPath(url.pathname);
    const entries = [...url.searchParams.entries()];
    const token = url.searchParams.get("token");
    if (
      innerPath !== "/driver-invitation" ||
      url.hash ||
      entries.length !== 1 ||
      entries[0]?.[0] !== "token" ||
      !token ||
      token.trim() !== token ||
      token.length < 8 ||
      token.length > 2048 ||
      /[\u0000-\u0020\u007f]/.test(token)
    ) {
      return null;
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

/**
 * Build the explicit sign-in destination for a validated invitation. Unlike
 * the ordinary post-sign-out recovery, this successful account-switch path
 * navigates directly to login so it cannot lose intent during a root reload.
 */
export function driverInvitationLoginPath(
  value: string | null | undefined,
  baseUrl = import.meta.env.BASE_URL || "/",
): string | null {
  const path = safeDriverInvitationPath(value);
  if (!path) return null;

  const url = new URL(path, "https://bookaclassic.invalid");
  const { market, language, innerPath } = parseLocaleFromPath(url.pathname);
  const localePrefix = buildLocalePrefix(market, language);
  const basePrefix = baseUrl.replace(/\/+$/, "");
  // AuthRedirect's Wouter navigation is relative to the locale/base router.
  // Pass its route-relative path in `next`; the localized login URL carries
  // the same market/language prefix without making Wouter append it twice.
  const next = `${innerPath}${url.search}`;
  return `${basePrefix}${localePrefix}/login?next=${encodeURIComponent(next)}`;
}

export function createDriverInvitationContinuation(
  value: string | null | undefined,
  now = Date.now(),
): string | null {
  const path = safeDriverInvitationPath(value);
  if (!path) return null;
  return JSON.stringify({
    path,
    expiresAt: now + DRIVER_INVITATION_CONTINUATION_TTL_MS,
  });
}

export function readDriverInvitationContinuation(
  stored: string | null | undefined,
  now = Date.now(),
): string | null {
  if (!stored) return null;
  try {
    const value = JSON.parse(stored) as { path?: unknown; expiresAt?: unknown };
    if (
      typeof value.expiresAt !== "number" ||
      !Number.isFinite(value.expiresAt) ||
      value.expiresAt <= now ||
      value.expiresAt > now + DRIVER_INVITATION_CONTINUATION_TTL_MS
    ) {
      return null;
    }
    return typeof value.path === "string"
      ? safeDriverInvitationPath(value.path)
      : null;
  } catch {
    return null;
  }
}